import { PermissionEnum } from '@Application/Security/Roles';
import ArchiveTemplateConfirmationDialog from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/ConfirmationDialogs/ArchiveTemplateConfirmationDialog';
import { openDocumentTemplateEditSidebar } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateEditSidebarSignal';
import { openDocumentTemplateHistorySidebar } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateHistorySidebarSignal';
import { openDocumentTemplateUploadSidebar } from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTemplateUploadSidebarSignal';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type DocumentTemplateView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplateView';
import { css } from '@emotion/react';
import { Button } from '@verclam/ui-kit';
import { FileUp, History, Pencil } from 'lucide-react';
import * as React from 'react';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
`;

type Props = {
	readonly template: DocumentTemplateView;
	readonly isAssigned: boolean;
	readonly organizationId: number;
};

/**
 * Leaf actions, each dispatches or opens its own sidebar signal — never a callback prop. "Nouvelle
 * version" and "Modifier" disappear once the gabarit is archived (archiving is definitive); the archive
 * action itself does too, since it is already done. "Historique" always renders: reading past versions
 * of an archived gabarit stays meaningful.
 */
const DocumentTemplateCardActions: React.FC<Props> = ({ template, isAssigned, organizationId }) => {
	const handleNewVersionClick = React.useCallback(() => {
		openDocumentTemplateUploadSidebar({ mode: 'version', templateId: template.id });
	}, [template.id]);

	const handleEditClick = React.useCallback(() => {
		openDocumentTemplateEditSidebar(template.id);
	}, [template.id]);

	const handleHistoryClick = React.useCallback(() => {
		openDocumentTemplateHistorySidebar(template.id);
	}, [template.id]);

	return (
		<div css={actionsRowStyle}>
			<ConditionalLoader data={!template.isArchived}>
				<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_WRITE}>
					<Button
						type="button"
						severity="secondary"
						outlined
						tooltip="Nouvelle version"
						tooltipOptions={{ position: 'top' }}
						icon={<FileUp size={16} />}
						onClick={handleNewVersionClick}
					/>
				</WithPermission>
				<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_WRITE}>
					<Button
						type="button"
						severity="secondary"
						outlined
						tooltip="Modifier"
						tooltipOptions={{ position: 'top' }}
						icon={<Pencil size={16} />}
						onClick={handleEditClick}
					/>
				</WithPermission>
			</ConditionalLoader>
			<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_READ}>
				<Button
					type="button"
					severity="secondary"
					text
					tooltip="Historique"
					tooltipOptions={{ position: 'top' }}
					icon={<History size={16} />}
					onClick={handleHistoryClick}
				/>
			</WithPermission>
			<ConditionalLoader data={!template.isArchived}>
				<WithPermission permission={PermissionEnum.DOCUMENT_TEMPLATE_ARCHIVE}>
					<ArchiveTemplateConfirmationDialog
						template={template}
						organizationId={organizationId}
						isAssigned={isAssigned}
					/>
				</WithPermission>
			</ConditionalLoader>
		</div>
	);
};

export default DocumentTemplateCardActions;
